/**
 * BTC Deposit History Component
 * Shows user's past and pending BTC deposits via Squid Router
 */

import { useState, useEffect } from 'react'
import { getBTCDepositHistory, getSquidStatus, updateBTCDepositStatus, type BTCDepositHistory as DepositType } from '../../services/squidRouter'
import { formatAddress as _formatAddress } from '../../utils/format'
import { Card, CardHeader, CardTitle, CardContent } from '../common/Card'

export function BTCDepositHistory() {
  const [deposits, setDeposits] = useState<DepositType[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Load deposits from local storage
  useEffect(() => {
    loadDeposits()
  }, [])

  const loadDeposits = () => {
    const history = getBTCDepositHistory()
    setDeposits(history)
    setIsLoading(false)
  }

  // Poll pending deposits
  useEffect(() => {
    const pendingDeposits = deposits.filter(
      d => d.status === 'deposited' || d.status === 'bridging'
    )

    if (pendingDeposits.length === 0) return

    // Track failures for each deposit
    const failureTracker: Record<string, number> = {}
    const MAX_FAILURES = 3

    const pollInterval = setInterval(async () => {
      for (const deposit of pendingDeposits) {
        try {
          const status = await getSquidStatus({
            chainflipStatusTrackingId: deposit.chainflipStatusTrackingId,
            bridgeType: 'chainflipmultihop',
          })

          // Reset failure count on success
          failureTracker[deposit.id] = 0

          if (status.squidTransactionStatus === 'success') {
            updateBTCDepositStatus(deposit.id, {
              status: 'completed',
              txHash: status.toChain?.transactionId,
              completedAt: Date.now(),
            })
            loadDeposits() // Refresh list
          } else if (status.squidTransactionStatus === 'failed') {
            updateBTCDepositStatus(deposit.id, {
              status: 'failed',
              errorMessage: status.error?.message || 'Transaction failed',
              completedAt: Date.now(),
            })
            loadDeposits() // Refresh list
          }
        } catch (error: any) {
          // Track 400 errors (invalid tracking ID)
          if (error?.status === 400) {
            failureTracker[deposit.id] = (failureTracker[deposit.id] || 0) + 1
            
            if (failureTracker[deposit.id] >= MAX_FAILURES) {
              // Mark as failed after repeated 400s
              updateBTCDepositStatus(deposit.id, {
                status: 'failed',
                errorMessage: 'Tracking ID not found. No BTC was sent to the deposit address.',
                completedAt: Date.now(),
              })
              loadDeposits()
              console.warn(`⚠️ [BTC History] Stopped polling deposit ${deposit.id} - invalid tracking ID`)
            }
          } else {
            console.warn('⚠️ [BTC History] Error polling deposit status:', error?.message || error)
          }
        }
      }
    }, 15000) // Poll every 15 seconds

    return () => clearInterval(pollInterval)
  }, [deposits])

  // Get status color
  const getStatusColor = (status: DepositType['status']) => {
    switch (status) {
      case 'pending':
        return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800'
      case 'deposited':
      case 'bridging':
        return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30'
      case 'completed':
        return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30'
      case 'failed':
        return 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30'
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800'
    }
  }

  // Get status label
  const getStatusLabel = (status: DepositType['status']) => {
    switch (status) {
      case 'pending':
        return 'Waiting for BTC'
      case 'deposited':
        return 'BTC Received'
      case 'bridging':
        return 'Bridging...'
      case 'completed':
        return 'Completed'
      case 'failed':
        return 'Failed'
      default:
        return status
    }
  }

  // Get status icon
  const getStatusIcon = (status: DepositType['status']) => {
    switch (status) {
      case 'pending':
      case 'deposited':
      case 'bridging':
        return (
          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        )
      case 'completed':
        return (
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        )
      case 'failed':
        return (
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        )
      default:
        return null
    }
  }

  if (isLoading) {
    return <div className="text-center py-4 text-gray-600 dark:text-gray-400">Loading...</div>
  }

  if (deposits.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>BTC Deposit History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
              No BTC deposits yet
            </p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">
              Use "Deposit BTC" to bridge native Bitcoin
            </p>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>BTC Deposit History</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {deposits.map((deposit) => (
            <div
              key={deposit.id}
              className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
            >
              {/* Header: Amount & Status */}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-orange-500" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.638 14.904c-1.602 6.43-8.113 10.34-14.542 8.736C2.67 22.05-1.244 15.525.362 9.105 1.962 2.67 8.475-1.243 14.9.358c6.43 1.605 10.342 8.115 8.738 14.546zm-6.35-4.613c.24-1.59-.974-2.45-2.64-3.03l.54-2.153-1.315-.33-.525 2.107c-.345-.087-.705-.167-1.064-.25l.526-2.127-1.32-.33-.54 2.165c-.285-.067-.565-.132-.84-.2l-1.815-.45-.35 1.407s.975.225.955.236c.535.136.63.486.615.766l-1.477 5.92c-.075.166-.24.406-.614.314.015.02-.96-.24-.96-.24l-.66 1.51 1.71.426.93.242-.54 2.19 1.32.327.54-2.17c.36.1.705.19 1.05.273l-.51 2.154 1.32.33.545-2.19c2.24.427 3.93.257 4.64-1.774.57-1.637-.03-2.58-1.217-3.196.854-.193 1.5-.76 1.68-1.93zm-3.01 4.22c-.404 1.64-3.157.75-4.05.53l.72-2.9c.896.23 3.757.67 3.33 2.37zm.41-4.24c-.37 1.49-2.662.735-3.405.55l.654-2.64c.744.18 3.137.524 2.75 2.084z"/>
                    </svg>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {parseFloat(deposit.btcAmount).toFixed(8)} BTC
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    → ~{parseFloat(deposit.estimatedOutput).toFixed(8)} {deposit.targetToken}
                  </p>
                </div>
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold ${getStatusColor(deposit.status)}`}>
                  {getStatusIcon(deposit.status)}
                  {getStatusLabel(deposit.status)}
                </span>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <p className="text-gray-600 dark:text-gray-400">Date:</p>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {new Date(deposit.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600 dark:text-gray-400">Target:</p>
                  <p className="font-medium text-gray-900 dark:text-white">{deposit.targetToken}</p>
                </div>
              </div>

              {/* Deposit Address (for pending) */}
              {deposit.status === 'pending' && (
                <div className="mt-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded border border-yellow-200 dark:border-yellow-800">
                  <p className="text-xs text-yellow-800 dark:text-yellow-200 font-semibold mb-1">
                    Send BTC to:
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs font-mono text-yellow-900 dark:text-yellow-100 break-all">
                      {deposit.depositAddress}
                    </code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(deposit.depositAddress)
                      }}
                      className="p-1 hover:bg-yellow-200 dark:hover:bg-yellow-800 rounded transition-colors"
                    >
                      <svg className="w-4 h-4 text-yellow-700 dark:text-yellow-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}

              {/* Transaction Hash (for completed) */}
              {deposit.txHash && (
                <div className="mt-3">
                  <a
                    href={`https://etherscan.io/tx/${deposit.txHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-brand-orange hover:text-brand-orange/80 font-semibold"
                  >
                    View on Etherscan
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              )}

              {/* Error Message (for failed) */}
              {deposit.errorMessage && (
                <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
                  <p className="text-xs text-red-800 dark:text-red-200">
                    {deposit.errorMessage}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

